import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  ChevronRight,
  Star,
  AlertTriangle,
  Send
} from "lucide-react";
import type { Applicant, ApplicationStage } from "@/types/applicant";
import { stageLabels, stageColors, stageOrder } from "@/types/applicant";

interface InvitationInfo {
  applicant_id: string;
  sent_at: string;
  response_status: string;
}

interface ApplicantPipelineProps {
  applicants: Applicant[];
  onUpdate: () => void;
}

const ApplicantPipeline = ({ applicants, onUpdate }: ApplicantPipelineProps) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [movingApplicant, setMovingApplicant] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<Record<string, InvitationInfo>>({});

  useEffect(() => {
    loadInvitations();
  }, [applicants]);

  const loadInvitations = async () => {
    if (applicants.length === 0) return;
    
    const { data } = await supabase
      .from("applicant_interview_invitations")
      .select("applicant_id, sent_at, response_status")
      .in("applicant_id", applicants.map(a => a.id))
      .order("sent_at", { ascending: false });

    if (data) {
      // Nur die letzte Einladung pro Bewerber speichern
      const invMap: Record<string, InvitationInfo> = {};
      data.forEach(inv => {
        if (!invMap[inv.applicant_id]) {
          invMap[inv.applicant_id] = inv;
        }
      });
      setInvitations(invMap);
    }
  };

  const getApplicantsByStage = (stage: ApplicationStage) => {
    return applicants.filter((a) => a.current_stage === stage);
  };

  // Prüfen ob Bewerber eine Nachverfolgung braucht (7 Tage ohne Aktivität)
  const needsFollowUp = (applicant: Applicant): { needs: boolean; daysSinceLastAction: number; lastNotified?: string } => {
    const now = new Date();
    const updatedAt = new Date(applicant.updated_at);
    const daysSinceUpdate = Math.floor((now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24));
    
    const invitation = invitations[applicant.id];
    const lastNotified = invitation?.sent_at;
    
    // Wenn es eine Einladung gibt und diese noch pending ist
    if (invitation && invitation.response_status === 'pending') {
      const invitationDate = new Date(invitation.sent_at);
      const daysSinceInvitation = Math.floor((now.getTime() - invitationDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysSinceInvitation >= 7) {
        return { needs: true, daysSinceLastAction: daysSinceInvitation, lastNotified };
      }
    }
    
    // Wenn keine Einladung oder beantwortet: Prüfe ob Status sich 7 Tage nicht geändert hat
    if (daysSinceUpdate >= 7) {
      return { needs: true, daysSinceLastAction: daysSinceUpdate, lastNotified };
    }
    
    return { needs: false, daysSinceLastAction: daysSinceUpdate, lastNotified };
  };

  const moveToNextStage = async (applicant: Applicant) => {
    const currentIndex = stageOrder.indexOf(applicant.current_stage);
    if (currentIndex >= stageOrder.length - 2) return; // Can't move past 'offer' automatically

    const nextStage = stageOrder[currentIndex + 1];
    if (nextStage === "rejected") return; // Skip rejected in auto-advance

    setMovingApplicant(applicant.id);
    try {
      const { error: updateError } = await supabase
        .from("applicants")
        .update({ current_stage: nextStage })
        .eq("id", applicant.id);

      if (updateError) throw updateError;

      await supabase.from("applicant_stage_history").insert({
        applicant_id: applicant.id,
        from_stage: applicant.current_stage,
        to_stage: nextStage,
        changed_by: profile?.full_name || "System",
      });

      toast.success(`Bewerber in "${stageLabels[nextStage]}" verschoben`);
      onUpdate();
    } catch (error) {
      console.error("Error moving applicant:", error);
      toast.error("Fehler beim Verschieben des Bewerbers");
    } finally {
      setMovingApplicant(null);
    }
  };

  const displayStages = stageOrder.filter(s => s !== "hired" && s !== "rejected");

  return (
    <ScrollArea className="w-full">
      <div className="flex gap-4 pb-4 min-w-max">
        {displayStages.map((stage) => {
          const stageApplicants = getApplicantsByStage(stage);
          return (
            <div key={stage} className="w-72 flex-shrink-0">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${stageColors[stage]}`} />
                      {stageLabels[stage]}
                    </CardTitle>
                    <Badge variant="secondary" className="text-xs">
                      {stageApplicants.length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {stageApplicants.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      Keine Bewerber
                    </p>
                  ) : (
                    stageApplicants.map((applicant) => {
                      const followUpStatus = needsFollowUp(applicant);
                      const invitation = invitations[applicant.id];
                      
                      return (
                      <Card
                        key={applicant.id}
                        className={`cursor-pointer hover:shadow-md transition-shadow ${followUpStatus.needs ? 'ring-2 ring-amber-400/50' : ''}`}
                        onClick={() => navigate(`/applicants/${applicant.id}`)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {applicant.first_name} {applicant.last_name}
                                </p>
                                {applicant.desired_position && (
                                  <p className="text-xs text-muted-foreground">
                                    {applicant.desired_position}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {followUpStatus.needs && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center">
                                        <AlertTriangle className="h-3 w-3 text-amber-600" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">
                                        {followUpStatus.daysSinceLastAction} Tage ohne Aktivität
                                        <br />
                                        <span className="text-muted-foreground">Bitte nachfassen!</span>
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {applicant.rating && (
                                <div className="flex items-center gap-1">
                                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                  <span className="text-xs">{applicant.rating}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              <span className="truncate">{applicant.email}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              <span>{applicant.phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>
                                {new Date(applicant.created_at).toLocaleDateString("de-DE")}
                              </span>
                            </div>
                            {invitation && (
                              <div className="flex items-center gap-1 text-primary">
                                <Send className="h-3 w-3" />
                                <span>
                                  Einladung: {new Date(invitation.sent_at).toLocaleDateString("de-DE")}
                                </span>
                              </div>
                            )}
                          </div>

                          {stage !== "offer" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full mt-2 h-7 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                moveToNextStage(applicant);
                              }}
                              disabled={movingApplicant === applicant.id}
                            >
                              <ChevronRight className="h-3 w-3 mr-1" />
                              Weiter
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};

export default ApplicantPipeline;
